<template>
  <div v-if="tabs">
    <template v-if="isMobile()">
      <tml-mobile-nav-bar
        :key="$route.fullPath"
        ref="mobileNavBar"
        name="studio-navigation"
        :nav-items="tabs"
        :click-callback="selectTab"
        :router-mode="false"
      />
    </template>
    <template v-else>
      <tml-tabbed-container
        ref="tabContainer"
        :show-body="false"
        :default-tab="-1"
      >
        <tml-tab-header
          ref="tabHeader"
          disable-header-slide
          type="vertical"
          position="right"
        >
          <tml-tab-header-item
            v-for="(tab, index) in tabs"
            :key="index"
            ref="navHeader"
            class="studio-tab-header-item w-full"
            :class="{
              'attention-grabber': tab.title === 'Save' && showPipOnSave,
              'atn-nice': tab.title === 'Save' && showPipOnSave,
              'atn-big': tab.title === 'Save' && showPipOnSave,
            }"
            :title="tab.title"
            :icon="tab.icon"
            :svg="tab.svg"
            disable-header-slide
            @click.stop="selectTab(null, index, tab)"
          />
        </tml-tab-header>
      </tml-tabbed-container>
    </template>

    <tml-tooltip
      v-if="showTooltip"
      name="tooltip-next-step-product"
      title="Choose product"
      :target="getTooltipTarget('product-list')"
      :ttl="0"
      disable-route-changes
      @dismissed="
        openTooltipOnceIfDoesNotHaveBadge('next-step-design', 'designer')
      "
    >
      Pick a style that you love
    </tml-tooltip>
    <tml-tooltip
      v-if="showTooltip"
      name="tooltip-next-step-design"
      title="Get creative"
      :target="getTooltipTarget('design-list')"
      :ttl="0"
      disable-route-changes
    >
      Remix templates or upload art
    </tml-tooltip>
    <tml-tooltip
      v-if="showTooltip"
      name="tooltip-next-step-save"
      title="Save up"
      :target="getTooltipTarget('save-popup')"
      :ttl="0"
      disable-route-changes
    >
      Edit product details & add photos
    </tml-tooltip>
  </div>
</template>

<script>
import {
  faShirt,
  faSave,
  faShoppingBag,
  faImage,
  faObjectGroup,
  faText,
  faStickyNote,
} from '@fortawesome/pro-light-svg-icons';

import {TmlStorage} from '@teemill/common/classes';

class StudioTab {
  title;
  component;
  icon;
  action;
  tags = [];

  constructor(title, component, icon, action, tags = []) {
    this.title = title;
    this.component = component;
    this.icon = icon;
    this.action = action;
    this.tags = tags;
  }
}

class StudioTabBuilder {
  tabs;

  constructor() {
    this.tabs = [];
  }

  when(condition, tab) {
    if (condition) {
      this.tabs.push(tab);
    }

    return this;
  }
}

export default {
  name: 'StudioNavigation',

  inject: [
    'isMobile',
    'getVuexProperty',
    'setVuexProperty',
    'showGetStarted',
    'config',
    'openTooltipOnce',
    'openTooltipOnceIfDoesNotHaveBadge',
  ],

  props: {
    showPipOnSave: {
      type: Boolean,
      default: false,
    },

    product: Boolean,
    design: Boolean,
    text: Boolean,
    save: Boolean,
    buy: Boolean,
    saveTemplate: Boolean,
    downloadAsFile: Boolean,
    drive: Boolean,
    stickers: Boolean,
    helpDismissed: Boolean,
    templates: Boolean,
    saveTemplateWithMetaContent: Boolean,
  },

  data() {
    return {
      /**
       * @param {Object[]} tabs A list of tabs to show in the TmlMobileNavBar
       */
      tabs: [
        {
          title: 'Product',
          component: 'product-list',
          icon: faShirt,
          classes: this.getProductNavItemClasses(),
          action: this.selectTab,
        },
        {
          title: 'Design',
          component: 'design-list',
          icon: faImage,
          action: this.selectTab,
        },
        {
          title: 'Text',
          component: 'text-editor',
          icon: faText,
          action: this.selectTab,
        },
      ],

      showTooltip: false,
    };
  },

  computed: {
    activeTab: {
      get() {
        return this.getVuexProperty('activeTab');
      },
    },

    activeTabIndex: {
      get() {
        let index = false;
        this.tabs.forEach((tab, i) => {
          if (tab.component === this.activeTab) {
            index = i;
          }
        });
        return index;
      },
    },
  },

  watch: {
    activeTab(value) {
      if (!value) {
        if (this.$refs.tabContainer) {
          this.$refs.tabContainer.setActiveTab(null);
        } else if (this.$refs.mobileNavBar) {
          this.$refs.mobileNavBar.resetActiveTab();
        }
      } else if (typeof this.activeTabIndex !== 'undefined') {
        if (this.$refs.tabContainer) {
          this.$refs.tabContainer.setActiveTab(this.activeTabIndex);
        } else if (this.$refs.mobileNavBar) {
          this.$refs.mobileNavBar.setActiveTab(this.activeTabIndex);
        }
      }
    },

    helpDismissed: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.initialiseTooltips();
        }
      },
    },
  },

  mounted() {
    this.buildTabs();
  },

  methods: {
    /**
     * Return a list of classes for the product nav item
     *
     * @return {String[]}
     */
    getProductNavItemClasses() {
      if (this.showGetStarted()) {
        return ['attention-grabber', 'atn-nice', 'atn-big'];
      }

      if (!TmlStorage.local.get('studioHasPreviouslyUsed')) {
        try {
          TmlStorage.local.set('studioHasPreviouslyUsed', true);
          return ['attention-grabber', 'atn-nice', 'atn-big'];
        } catch (e) {
          /**
           * There is a bug in Safari 10 and below private browsing mode that
           * causes window.localStorage to throw an error
           */
          return ['attention-grabber', 'atn-nice', 'atn-big'];
        }
      }
      return [];
    },

    /**
     * Navigate to a tab via component name
     *
     * @param {String} tabCode The component corresponding to the target tab
     * @return {void}
     */
    goToTab(tabCode) {
      Object.keys(this.tabs).forEach(t => {
        const tab = this.tabs[t];
        if (tab.component === tabCode) {
          this.selectTab(null, null, tab);
        }
      });
    },

    /**
     * Navigate to a tab
     *
     * @param {Event} event A browser event
     * @param {Number} key The key of the tab to select
     * @param {Object} tab The target tab
     * @return {void}
     */
    selectTab(event, key, tab) {
      if (this.activeTab === tab.component) {
        this.setVuexProperty('activeTab', null);
      } else {
        this.setVuexProperty('activeTab', tab.component);
      }
      this.$emit('tab-selected', tab);
    },

    getTooltipTarget(key) {
      let index = 0;
      this.tabs.forEach((tab, i) => {
        if (tab.component === key) {
          index = i;
        }
      });

      if (this.$refs.mobileNavBar) {
        return this.$refs.mobileNavBar.$el.getElementsByClassName('nav-item')[
          index
        ];
      }
      if (this.$refs.navHeader) {
        return this.$refs.navHeader[index]?.$el;
      }
      return this.$el;
    },

    initialiseTooltips() {
      if (this.product && !this.showTooltip) {
        this.showTooltip = true;
        if (
          this.showTooltip &&
          this.config.showFirstTimeTooltips === 'dashboard'
        ) {
          this.openTooltipOnceIfDoesNotHaveBadge(
            'next-step-product',
            'designer'
          );
        }
      }
    },

    buildTabs() {
      const builder = new StudioTabBuilder();

      builder
        .when(
          this.product,
          new StudioTab('Product', 'product-list', faShirt, this.selectTab)
        )
        .when(
          this.templates,
          new StudioTab(
            'Templates',
            'posta-templates',
            faObjectGroup,
            this.selectTab,
            ['design']
          )
        )
        .when(
          this.design,
          new StudioTab('Design', 'design-list', faImage, this.selectTab, [
            'design',
          ])
        )
        .when(
          this.drive,
          new StudioTab('Images', 'drive-tab', faImage, this.selectTab, [
            'design',
          ])
        )
        .when(
          this.stickers,
          new StudioTab(
            'Stickers',
            'stickers-design-list',
            faStickyNote,
            this.selectTab,
            ['design']
          )
        )
        .when(
          this.text,
          new StudioTab('Text', 'text-editor', faText, this.selectTab, [
            'design',
          ])
        )
        .when(
          this.save,
          new StudioTab('Save', 'save-popup', faSave, this.selectTab, [
            'export',
          ])
        )
        .when(
          this.buy,
          new StudioTab('Order', 'buy-popup', faShoppingBag, this.selectTab, [
            'export',
            'ecom',
          ])
        )
        .when(
          this.saveTemplate,
          new StudioTab(
            'Save',
            'save-template-popup',
            faObjectGroup,
            this.selectTab,
            ['export']
          )
        )
        .when(
          this.downloadAsFile,
          new StudioTab('Save', 'download-as-file', faSave, this.selectTab, [
            'export',
          ])
        )
        .when(
          this.saveTemplateWithMetaContent,
          new StudioTab(
            'Save',
            'save-template-with-meta-content',
            faSave,
            this.selectTab,
            ['export']
          )
        );

      this.tabs = builder.tabs;
    },
  },
};
</script>

<style lang="scss">
.studio-tab-header-item {
  padding: 2em 0 !important;

  &.tab-item:not(.active) {
    * {
      color: var(--tml-text-color) !important;
    }
    .svg-icon svg path {
      stroke: var(--tml-text-color) !important;
    }
  }
}
</style>

<style scoped lang="scss">
.attention-grabber {
  position: relative;

  &::before {
    content: '';
    background-color: var(--tml-error-color);
    position: absolute;
    top: 20%;
    right: 20%;
    width: 4px;
    height: 4px;
    border-radius: 100%;
  }

  &.atn-nice::before {
    background-color: var(--tml-primary-color);
  }

  &.atn-big::before {
    transform: scale(2.5);
  }
}
</style>
